// Batch 30 · M76 — "Possible duplicate of DOC-xxxx" for two DIFFERENT files read as the same document (same type, same
// reference number, same party). A warning only — no block, no confirm step. "Not a duplicate" (with a reason, Audit
// Trail row) clears the pair on both documents for good. Also a small badge on a vouching item a flagged document is
// linked to (display only).
import React from "react";
import api, { toErrMsg } from "@/lib/api";
import { askPrompt } from "@/lib/appDialog";
import { asArray } from "@/lib/asArray";
import { toast } from "sonner";

export const DRD_TEXT = {
  en: {
    badge: "Possible duplicate of {ref}",
    amountDiffers: "amount differs",
    notDuplicate: "Not a duplicate",
    reasonPrompt: "Why are {a} and {b} not the same document? The reason is kept in the Audit Trail.",
    reasonTitle: "Not a duplicate",
    cleared: "Marked as not a duplicate.",
    panelTitle: "Possible duplicate",
    panelBody: "Same type, same reference number and same party as {ref} — a different file. Check that the item is not recorded twice.",
    itemBadge: "Linked document may be a duplicate: {refs}",
  },
  ar: {
    badge: "تكرار محتمل لـ {ref}",
    amountDiffers: "المبلغ مختلف",
    notDuplicate: "ليس تكراراً",
    reasonPrompt: "لماذا لا يُعد {a} و{b} المستند نفسه؟ يُحفظ السبب في سجل التدقيق.",
    reasonTitle: "ليس تكراراً",
    cleared: "عُلّم بأنه ليس تكراراً.",
    panelTitle: "تكرار محتمل",
    panelBody: "النوع نفسه ورقم المرجع نفسه والطرف نفسه مثل {ref} — ملف مختلف. تحقق من أن البند غير مسجل مرتين.",
    itemBadge: "قد يكون المستند المرتبط مكرراً: {refs}",
  },
};

const T = (lang) => DRD_TEXT[lang === "ar" ? "ar" : "en"];

export function refDuplicates(doc) {
  return asArray(doc && doc.ref_duplicates).filter((x) => x && x.id);
}

/** Badges on a Document Review row: one per flagged partner document. */
export function DocRefDuplicateBadges({ doc, lang }) {
  const L = T(lang);
  return refDuplicates(doc).map((x) => (
    <span key={x.id} className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800"
          data-testid={`doc-ref-dup-${doc.id}-${x.id}`}>
      {L.badge.replace("{ref}", x.doc_ref || x.id.slice(0, 8))}
      {x.amount_differs && <span className="ms-1 font-normal" data-testid={`doc-ref-dup-amount-${doc.id}-${x.id}`}>· {L.amountDiffers}</span>}
    </span>
  ));
}

/** The block in the document panel, with "Not a duplicate" for each flagged partner. */
export function DocRefDuplicatePanel({ doc, lang, onChanged }) {
  const L = T(lang);
  const rows = refDuplicates(doc);
  if (!rows.length) return null;
  const clear = async (x) => {
    const me = doc.doc_ref || doc.id.slice(0, 8);
    const reason = await askPrompt(L.reasonPrompt.replace("{a}", me).replace("{b}", x.doc_ref || x.id.slice(0, 8)), "",
                                   { title: L.reasonTitle, okLabel: L.notDuplicate, multiline: true });
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/documents/${doc.id}/not-duplicate`, { other_doc_id: x.id, reason: reason.trim() }, { _silent: true });
      toast.success(L.cleared);
      if (onChanged) onChanged();
    } catch (e) { toast.error(toErrMsg(e, L.notDuplicate)); }
  };
  return (
    <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
         role="status" data-testid="doc-ref-dup-panel">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider">{L.panelTitle}</div>
      {rows.map((x) => (
        <div key={x.id} className="flex flex-wrap items-center gap-2 py-0.5" data-testid={`doc-ref-dup-panel-${x.id}`}>
          <span>{L.panelBody.replace("{ref}", x.doc_ref || x.id.slice(0, 8))}{x.amount_differs ? ` (${L.amountDiffers})` : ""}</span>
          <button type="button" className="rounded border border-amber-400 px-1.5 py-0.5 text-[10px] hover:bg-amber-100"
                  onClick={() => clear(x)} data-testid={`doc-not-duplicate-${x.id}`}>{L.notDuplicate}</button>
        </div>
      ))}
    </div>
  );
}

/** The small badge on a vouching item whose linked document is flagged (display only). */
export function VouchingDuplicateBadge({ item, lang }) {
  const refs = asArray(item && item.possible_duplicate_docs);
  if (!refs.length) return null;
  const text = T(lang).itemBadge.replace("{refs}", refs.join(", "));
  return (
    <span className="ms-1 inline-flex rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-800" title={text}
          data-testid={`vouch-doc-dup-${item.id}`}>⚠ {refs.join(", ")}</span>
  );
}
